import React, { useState, useEffect } from "react";
import {
    Container,
    Grid,
    Paper,
    CardContent,
    Typography,
    Avatar,
    Box,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import WindowIcon from "@mui/icons-material/Window";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";

import WaveguideLightIcon from "../components/Icons/WaveguideLightIcon.svg";
import WaveguideDarkIcon from "../components/Icons/WaveguideDarkIcon.svg";

// Import services and data
import DoorDataService from "../services/Door.services";
import PanelDataService from "../services/Panel.services";
import WaveguideDataService from "../services/Waveguide.services";
import RFWindowDataService from "../services/RFWindow.services";

export default function WorkOrders(props) {
    const { user, token, handleOpenSnackbar, darkState } = props;
    const navigate = useNavigate();

    // State for door count, loading, and door data
    const [doorCount, setDoorCount] = useState(0);
    const [doorOrderList, setDoorOrderList] = useState([]);
    const [isLoadingDoors, setIsLoadingDoors] = useState(true);

    // Waveguide API state
    const [waveguideCount, setWaveguideCount] = useState(0);
    const [totalWaveguideItems, setTotalWaveguideItems] = useState(0);
    const [waveguideOrderList, setWaveguideOrderList] = useState([]);
    const [isLoadingWaveguides, setIsLoadingWaveguides] = useState(true);

    // RF Windows API state
    const [windowsCount, setWindowsCount] = useState(0);
    const [totalWindowItems, setTotalWindowItems] = useState(0);
    const [windowOrderList, setWindowOrderList] = useState([]);
    const [isLoadingWindows, setIsLoadingWindows] = useState(true);

    // Panel API state
    const [panelCount, setPanelCount] = useState(0);
    const [totalPanelItems, setTotalPanelItems] = useState(0);
    const [panelOrderList, setPanelOrderList] = useState([]);
    const [isLoadingPanels, setIsLoadingPanels] = useState(true);

    // Fetch door count on component mount
    useEffect(() => {
        const fetchDoorCount = async () => {
            if (token) {
                setIsLoadingDoors(true);
                try {
                    const response = await DoorDataService.getAll(token);
                    if (response.data) {
                        setDoorOrderList(response.data);
                        setDoorCount(response.data.length);
                    }
                } catch (error) {
                    console.error("Error fetching door count:", error);
                    // Keep default count of 0 if fetch fails
                } finally {
                    setIsLoadingDoors(false);
                }
            } else {
                setIsLoadingDoors(false);
            }
        };

        fetchDoorCount();
    }, [token]);

    // Fetch waveguide counts on component mount
    useEffect(() => {
        const fetchWaveguideCounts = async () => {
            if (!token) {
                setIsLoadingWaveguides(false);
                return;
            }
            setIsLoadingWaveguides(true);
            try {
                const response = await WaveguideDataService.getAllWaveguides(
                    token
                );
                const data = Array.isArray(response.data) ? response.data : [];
                setWaveguideOrderList(data);
                const visible = data.filter((w) => !w.is_archived);
                setWaveguideCount(visible.length);
                const itemsTotal = visible.reduce((sum, w) => {
                    const itemSum = Array.isArray(w.items)
                        ? w.items.reduce(
                              (acc, item) => acc + (Number(item.quantity) || 0),
                              0
                          )
                        : 0;
                    return sum + itemSum;
                }, 0);
                setTotalWaveguideItems(itemsTotal);
            } catch (error) {
                console.error("Error fetching waveguide count:", error);
                setWaveguideOrderList([]);
                setWaveguideCount(0);
                setTotalWaveguideItems(0);
            } finally {
                setIsLoadingWaveguides(false);
            }
        };

        fetchWaveguideCounts();
    }, [token]);

    // Fetch RF window counts on component mount
    useEffect(() => {
        const fetchWindowCounts = async () => {
            if (!token) {
                setIsLoadingWindows(false);
                return;
            }
            setIsLoadingWindows(true);
            try {
                const response = await RFWindowDataService.getAllRFWindows(
                    token
                );
                const data = Array.isArray(response.data) ? response.data : [];
                setWindowOrderList(data);
                const visible = data.filter((w) => !w.is_archived);
                setWindowsCount(visible.length);
                const itemsTotal = visible.reduce((sum, w) => {
                    return sum + (Number(w.quantity) || 0);
                }, 0);
                setTotalWindowItems(itemsTotal);
            } catch (error) {
                console.error("Error fetching RF window count:", error);
                setWindowOrderList([]);
                setWindowsCount(0);
                setTotalWindowItems(0);
            } finally {
                setIsLoadingWindows(false);
            }
        };

        fetchWindowCounts();
    }, [token]);

    // Fetch panel counts on component mount
    useEffect(() => {
        const fetchPanelCounts = async () => {
            if (!token) {
                setIsLoadingPanels(false);
                return;
            }
            setIsLoadingPanels(true);
            try {
                const response = await PanelDataService.getAllPanels(token);
                const data = Array.isArray(response.data) ? response.data : [];
                setPanelOrderList(data);
                const visible = data.filter((p) => !p.is_archived);
                setPanelCount(visible.length);
                const itemsTotal = visible.reduce((sum, p) => {
                    const itemSum = Array.isArray(p.items)
                        ? p.items.reduce(
                              (acc, item) => acc + (Number(item.quantity) || 0),
                              0
                          )
                        : 0;
                    return sum + itemSum;
                }, 0);
                setTotalPanelItems(itemsTotal);
            } catch (error) {
                console.error("Error fetching panel count:", error);
                setPanelOrderList([]);
                setPanelCount(0);
                setTotalPanelItems(0);
            } finally {
                setIsLoadingPanels(false);
            }
        };

        fetchPanelCounts();
    }, [token]);

    const workOrderCards = [
        {
            title: "DOORS",
            description: isLoadingDoors
                ? "Loading..."
                : `${doorCount} Door Work Orders`,
            isLoading: isLoadingDoors,
            icon: <MeetingRoomIcon />,
            path: "/asset/doors",
        },
        {
            title: "WAVEGUIDES",
            description: isLoadingWaveguides
                ? "Loading..."
                : `${waveguideCount} Waveguide Work Orders | ${totalWaveguideItems} Total Waveguides`,
            isLoading: isLoadingWaveguides,
            icon: (
                <Avatar
                    sx={{ width: "30px", height: "30px" }}
                    src={darkState ? WaveguideDarkIcon : WaveguideLightIcon}
                    variant="square"
                />
            ),
            path: "/asset/waveguides",
        },
        {
            title: "WINDOWS",
            description: isLoadingWindows
                ? "Loading..."
                : `${windowsCount} Window Work Orders | ${totalWindowItems} Total Windows`,
            isLoading: isLoadingWindows,
            icon: <AspectRatioIcon />,
            path: "/asset/windows",
        },
        {
            title: "PANELS",
            description: isLoadingPanels
                ? "Loading..."
                : `${panelCount} Panel Work Orders | ${totalPanelItems} Total Panels`,
            isLoading: isLoadingPanels,
            icon: <ViewModuleIcon />,
            path: "/asset/panels",
        },
        {
            title: "SCAN QR CODE",
            description: "Scan a QR code to open a work order",
            icon: <QrCodeScannerIcon />,
            path: "/qr-code-scan",
        },
    ];

    const handleCardClick = (card) => {
        // Don't allow navigation if this is the doors card and it's still loading
        if (card.title === "DOORS" && card.isLoading) {
            return; // Do nothing while loading
        }
        if (card.title === "WAVEGUIDES" && card.isLoading) {
            return;
        }
        if (card.title === "WINDOWS" && card.isLoading) {
            return;
        }

        // Handle QR Code scan page
        if (card.title === "SCAN QR CODE") {
            navigate("/qr-code-scan");
            return;
        }

        if (card.path) {
            // If this is the doors path and we have door data, pass it as state
            if (card.path === "/asset/doors" && doorCount > 0) {
                navigate(card.path, {
                    state: {
                        doorOrderList: doorOrderList,
                        skipInitialFetch: true,
                    },
                });
            } else if (
                card.path === "/asset/waveguides" &&
                waveguideCount > 0
            ) {
                navigate(card.path, {
                    state: {
                        waveguideOrderList: waveguideOrderList,
                        skipInitialFetch: true,
                    },
                });
            } else if (card.path === "/asset/windows" && windowsCount > 0) {
                navigate(card.path, {
                    state: {
                        windowOrderList: windowOrderList,
                        skipInitialFetch: true,
                    },
                });
            } else {
                navigate(card.path);
            }
        } else {
            handleOpenSnackbar("info", `${card.title} coming soon...`);
        }
    };

    return (
        <Container
            maxWidth="lg"
            sx={{
                py: 4,
            }}
        >
            <Typography variant="h4" component="h1" gutterBottom>
                Work Orders
            </Typography>
            <Grid container spacing={3}>
                {workOrderCards.map((card, index) => (
                    <Grid
                        sx={{ boxShadow: 0 }}
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        key={index}
                    >
                        <Paper
                            elevation={0}
                            onClick={() => handleCardClick(card)}
                            sx={{
                                maxWidth: "500px",
                                border: darkState ? 1.5 : 3,
                                borderColor: "primary.main",
                                borderRadius: "16px",
                                cursor: "pointer",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                },
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 2,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: "primary.main",
                                            mr: 2,
                                            width: 48,
                                            height: 48,
                                        }}
                                    >
                                        {card.icon}
                                    </Avatar>
                                    <Typography variant="h6" component="h2">
                                        {card.title}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {card.isLoading && (
                                            <CircularProgress size={16} />
                                        )}
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {card.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}
